import {Button, openDeleteDialog, openDialog} from 'platform/components';
import {HStack, VStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {head} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteAccountingMatrixDatagridRecordMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {testIds} from '@dms/routes';
import {catchUnhandledDataGridActions, DmsDataGrid, handleApiError} from '@dms/shared';

import type {ActionCallback} from 'features/eag-grid';

import {
  AccountingMatrixDatagridConfig,
  getAccountingMatrixDatagridGridCode,
} from '../utils/accountingMatrixDatagridConfig';
import {AccountingMatrixDatagridForm} from './AccountingMatrixDatagridForm';

interface AccountingMatrixDatagridGridProps {
  config: AccountingMatrixDatagridConfig;
}

/**
 * The grid (columns, filters, row data, available row actions) is entirely BE-defined — see
 * T20-134805. This component only mounts DmsDataGrid by gridCode and wires the create/edit
 * dialog form + delete; it must never define its own columns/rows.
 */
export function AccountingMatrixDatagridGrid(props: AccountingMatrixDatagridGridProps) {
  const {dgCode} = props.config;
  const gridCode = getAccountingMatrixDatagridGridCode(dgCode);

  const [deleteRecord] = useDeleteAccountingMatrixDatagridRecordMutation();

  const openRecordForm = (recordId?: string) =>
    openDialog(
      <AccountingMatrixDatagridForm
        config={props.config}
        recordId={recordId}
        data-testid={testIds.settings.accountingMatrixManagement(`${dgCode}-form`)}
      />,
      {
        title: recordId ? i18n.t('general.labels.edit') : i18n.t('general.actions.addNew'),
        'data-testid': testIds.settings.accountingMatrixManagement(`${dgCode}-form-dialog`),
      }
    );

  const actionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? head(rowId) : rowId;
    if (!id) {
      return;
    }

    match(actionKey)
      .with('edit', 'redirectDetail', () => openRecordForm(id))
      .with('delete', () => {
        openDeleteDialog({
          text: i18n.t(
            'page.accounting.labels.accountingMatrixDatagridsRecordDeleteConfirmationText'
          ),
          onConfirm: () =>
            deleteRecord({dgCode, id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        });
      })
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  return (
    <VStack spacing={4}>
      <HStack justify="flex-end">
        <Button
          variant="primary"
          title={i18n.t('general.actions.addNew')}
          onClick={() => openRecordForm()}
          data-testid={testIds.settings.accountingMatrixManagement(`${dgCode}-addNew`)}
        />
      </HStack>
      <DmsDataGrid
        gridCode={gridCode}
        actionCallback={actionCallback}
        data-testid={testIds.settings.accountingMatrixManagement(`${dgCode}-grid`)}
      />
    </VStack>
  );
}
